import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import type { EmbedMessage } from '../embed';
import { useFlowReporter } from '../embed/useFlowReporter';
import { ChatWidget } from '../flow/ChatWidget';
import { decodeFlowCode } from '../flow/flowLink';
import { FlowRunner } from '../flow/FlowRunner';
import { loadSdkParent, type SdkParent } from '../sdk/child';
import { EmptySession } from '../ui/EmptySession';

/**
 * The flow inside the web SDK's popup (or iframe): `/sdk/u/<code>`.
 *
 * Same flow and same `trustsy:*` events as `/embed`, delivered through the
 * parent's `onFlowEvent` prop instead of `postMessage` — zoid carries them and
 * checks which window they come from. The redirect return leg is off: the
 * partner page that opened this window is where the applicant goes back to.
 * Opened directly (not by the SDK), it is the ordinary `/u/<code>` link.
 */
export function SdkScreen({ load = loadSdkParent }: { load?: () => Promise<SdkParent> }) {
  const { code } = useParams<{ code: string }>();
  const token = code ? decodeFlowCode(code.trim()) : null;
  const [parent, setParent] = useState<SdkParent | undefined>(undefined);

  useEffect(() => {
    let live = true;
    load().then(
      (found) => live && setParent(found),
      () => live && setParent(null),
    );
    return () => {
      live = false;
    };
  }, [load]);

  const send = useMemo(
    () =>
      parent
        ? (message: EmbedMessage) => {
            // A parent that navigated away rejects the call; the flow goes on.
            try {
              void Promise.resolve(parent.onFlowEvent(message)).catch(() => undefined);
            } catch {
              // Same: nobody is listening any more.
            }
          }
        : null,
    [parent],
  );
  const { onSnapshot, onLoadError } = useFlowReporter(send);

  if (!token) return <EmptySession kind="missing" />;
  if (parent === undefined) return null;
  if (parent === null) return <Navigate replace to={`/u/${code}`} />;

  return (
    <>
      <FlowRunner
        onLoadError={onLoadError}
        onSnapshot={onSnapshot}
        returnLeg={false}
        token={token}
        withHandoff
      />
      <ChatWidget token={token} />
    </>
  );
}
